import type {
  UnderwritingAgentStatus,
  UnderwritingReportKey
} from "api/underwriting-agents/types";
import { getReportLabel } from "../rules";

/** Where the agent stands on one applicant's report, as the Agent review modal lists it. */
export type ReportReviewState =
  | "running"
  | "failed"
  | "to-review"
  | "reviewed"
  | "none";

export const getReportReviewState = (
  status: UnderwritingAgentStatus,
  applicantId: number,
  reportKey: UnderwritingReportKey
): { state: ReportReviewState; toReview: number } => {
  const report = status.reports.find(
    (item) => item.applicant_id === applicantId && item.report_key === reportKey
  );
  if (!report) return { state: "none", toReview: 0 };

  const toReview = report.to_review_count;
  if (report.status === "running") return { state: "running", toReview };
  if (report.status === "failed") return { state: "failed", toReview };
  if (toReview > 0) return { state: "to-review", toReview };

  return { state: report.reviewed_count > 0 ? "reviewed" : "none", toReview };
};

/**
 * The agent is still working: a report is running, or it hasn't checked any yet (a report gets a check only once
 * it's ready to review, so the status lists none at first).
 */
export const isReviewInProgress = (status: UnderwritingAgentStatus) =>
  status.enabled && (status.running_count > 0 || status.reports.length === 0);

const getTooltip = ({
  reviewing,
  done,
  total,
  toReview,
  findingsLine,
  failedReports,
  nothingFlagged
}: {
  reviewing: boolean;
  done: number;
  total: number;
  toReview: number;
  findingsLine: string;
  failedReports: string[];
  nothingFlagged: boolean;
}) => {
  const reportsLine = `${total} ${total === 1 ? "report" : "reports"}`;
  if (total === 0)
    return "Reviewing each report as soon as it's ready. Findings show up here as soon as they're flagged.";
  if (reviewing)
    return toReview > 0
      ? `Reviewed ${done} of ${total} reports so far. ${findingsLine}; more may show up as the rest are reviewed.`
      : `Reviewing each report as soon as it's ready: ${done} of ${total} done. Findings show up here as soon as they're flagged.`;
  if (failedReports.length > 0)
    return `Couldn't review ${failedReports.join(", ")}. ${
      toReview > 0 ? `${findingsLine}.` : "Nothing else to review."
    }`;
  if (toReview > 0) return `All ${reportsLine} reviewed. ${findingsLine}.`;
  if (nothingFlagged)
    return `All ${reportsLine} reviewed. Nothing was flagged.`;

  return `All ${reportsLine} reviewed. Your team has reviewed every finding.`;
};

/** What the Agent review button's ring and tooltip say about the whole application. */
export const getReviewProgress = (status: UnderwritingAgentStatus) => {
  const total = status.reports.length;
  const done = total - status.running_count;
  const toReview = status.to_review_count;
  const findingsLine = `${toReview} ${toReview === 1 ? "finding" : "findings"} to review`;
  const failedReports = status.reports
    .filter((report) => report.status === "failed")
    .map((report) => getReportLabel(report.report_key));
  const reviewing = isReviewInProgress(status);

  const tooltip = getTooltip({
    reviewing,
    done,
    total,
    toReview,
    findingsLine,
    failedReports: [...new Set(failedReports)],
    nothingFlagged: status.reviewed_count === 0
  });

  return {
    reviewing,
    fraction: total > 0 ? done / total : 0,
    toReview,
    tooltip
  };
};
